// ─────────────────────────────────────────────────────────────
// ROISummaryWidget
//
// Compact dashboard widget showing net ROI across all prop firms.
// Fetches its own payouts + expenses data. Clicking navigates to
// the full ROI Tracker in the Portfolio section.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n || 0);

export function ROISummaryWidget({ userId, accounts, setView }) {
  const navigate = useNavigate();
  // Defensive guard — if auth fails, accounts may not be an array.
  // Return null silently rather than crashing the whole app.
  const safeAccounts = Array.isArray(accounts) ? accounts : [];

  const [payouts,  setPayouts]  = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const fetch$ = async () => {
      try {
        const [pRes, eRes] = await Promise.all([
          authFetch(`${SUPABASE_URL}/rest/v1/payouts?user_id=eq.${userId}&select=amount,account_id`),
          authFetch(`${SUPABASE_URL}/rest/v1/expenses?user_id=eq.${userId}&select=amount,account_id`),
        ]);
        const [pd, ed] = await Promise.all([pRes.json(), eRes.json()]);
        if (cancelled) return;
        setPayouts(Array.isArray(pd) ? pd : []);
        setExpenses(Array.isArray(ed) ? ed : []);
      } catch (e) {
        console.error("[ROISummaryWidget]", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch$();
    return () => { cancelled = true; };
  }, [userId]);

  const totalPayouts  = payouts.reduce((s, p)  => s + (parseFloat(p.amount)  || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalNet      = totalPayouts - totalExpenses;
  const roi           = totalExpenses > 0 ? (totalNet / totalExpenses) * 100 : null;

  // Per-firm rollup
  const firmMap = {};
  safeAccounts.forEach(a => {
    const firm = a.prop_firm || "Other";
    if (!firmMap[firm]) firmMap[firm] = { payouts: 0, expenses: 0 };
  });
  payouts.forEach(p => {
    const acct = safeAccounts.find(a => a.id === p.account_id);
    const firm = acct?.prop_firm || "Other";
    if (!firmMap[firm]) firmMap[firm] = { payouts: 0, expenses: 0 };
    firmMap[firm].payouts += parseFloat(p.amount) || 0;
  });
  expenses.forEach(e => {
    const acct = safeAccounts.find(a => a.id === e.account_id);
    const firm = acct?.prop_firm || "Other";
    if (!firmMap[firm]) firmMap[firm] = { payouts: 0, expenses: 0 };
    firmMap[firm].expenses += parseFloat(e.amount) || 0;
  });

  const firms = Object.entries(firmMap)
    .map(([name, d]) => ({ name, net: d.payouts - d.expenses, payouts: d.payouts, expenses: d.expenses }))
    .filter(f => f.payouts > 0 || f.expenses > 0)
    .sort((a, b) => b.net - a.net);

  // Don't render if no data yet
  if (!loading && totalPayouts === 0 && totalExpenses === 0) return null;

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <SectionTitle>💰 Prop Firm ROI</SectionTitle>
        <button
          onClick={() => navigate("/portfolio/roi")}
          style={{ background: "none", border: "none", color: C.blue, fontSize: 12, cursor: "pointer" }}
        >
          Full Tracker →
        </button>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>
      ) : (
        <>
          {/* Summary row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 14 }}>
            {[
              { label: "Payouts",  value: fmt(totalPayouts),  color: C.green },
              { label: "Expenses", value: fmt(totalExpenses), color: C.red   },
              { label: "Net ROI",  value: roi !== null ? `${roi >= 0 ? "+" : ""}${roi.toFixed(0)}%` : fmt(totalNet), color: totalNet >= 0 ? C.green : C.red },
            ].map(k => (
              <div
                key={k.label}
                style={{
                  background: "#1a1d2e",
                  borderRadius: 8,
                  padding: "10px 12px",
                  textAlign: "center",
                  border: `1px solid ${C.border}`,
                }}
              >
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                  {k.label}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: k.color, fontFamily: "monospace" }}>
                  {k.value}
                </div>
              </div>
            ))}
          </div>

          {/* Per-firm rows */}
          {firms.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {firms.slice(0, 4).map(f => {
                const firmRoi = f.expenses > 0 ? ((f.net / f.expenses) * 100) : null;
                const barPct  = totalExpenses > 0 ? Math.min(100, Math.abs(f.payouts / totalPayouts) * 100) : 0;
                return (
                  <div key={f.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 80, fontSize: 11, color: C.muted, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.name}
                    </div>
                    {/* Progress bar */}
                    <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{
                        height: "100%",
                        width: `${barPct}%`,
                        background: f.net >= 0 ? C.green : C.red,
                        borderRadius: 3,
                        transition: "width 0.4s ease",
                      }} />
                    </div>
                    <div style={{
                      fontSize: 12, fontWeight: 700,
                      color: f.net >= 0 ? C.green : C.red,
                      fontFamily: "monospace",
                      width: 70,
                      textAlign: "right",
                      flexShrink: 0,
                    }}>
                      {fmt(f.net)}
                    </div>
                    {firmRoi !== null && (
                      <div style={{
                        fontSize: 10, color: firmRoi >= 0 ? C.green : C.red,
                        width: 40, textAlign: "right", flexShrink: 0,
                      }}>
                        {firmRoi >= 0 ? "+" : ""}{firmRoi.toFixed(0)}%
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
